import { useState, useEffect, useCallback } from 'react';
import { getProjectCommunications } from '../services/clientCommunicationService';
import type { ClientCommunicationView } from '../types';

interface UseProjectCommunicationsOptions {
  projectId: string | null;
  autoFetch?: boolean;
}

interface UseProjectCommunicationsReturn {
  communications: ClientCommunicationView[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useProjectCommunications({
  projectId,
  autoFetch = true,
}: UseProjectCommunicationsOptions): UseProjectCommunicationsReturn {
  const [communications, setCommunications] = useState<ClientCommunicationView[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCommunications = useCallback(async () => {
    if (!projectId) {
      setCommunications([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await getProjectCommunications(projectId);
      setCommunications(data);
    } catch (err) {
      console.error('Error fetching project communications:', err);
      setError(err instanceof Error ? err.message : 'Failed to load communications');
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (autoFetch) {
      fetchCommunications();
    }
  }, [autoFetch, fetchCommunications]);

  return {
    communications,
    isLoading,
    error,
    refetch: fetchCommunications,
  };
}
